import React, { useState, useEffect, useRef, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Slider } from "../ui/slider";
import { Button } from "../ui/button";
import { TrendingUp, Plus } from "lucide-react";

export function MarketAnalysisSection({
  marketValuePerSqft,
  setMarketValuePerSqft,
  readOnly = false,
  onAddClick,
  showAddButton = false,
  children,
}) {
  // Local state for responsive slider/input — updates instantly
  const [localValue, setLocalValue] = useState(marketValuePerSqft);
  const [inputValue, setInputValue] = useState(String(marketValuePerSqft));
  const debounceRef = useRef(null);
  const isLocalChange = useRef(false);

  // Debounced parent update — only triggers heavy recalculation after user stops
  const debouncedUpdate = useCallback(
    (val) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        setMarketValuePerSqft(val);
      }, 300);
    },
    [setMarketValuePerSqft]
  );

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  // Sync local state when external value changes (e.g. from nearby properties)
  useEffect(() => {
    if (!isLocalChange.current) {
      setLocalValue(marketValuePerSqft);
      setInputValue(String(marketValuePerSqft));
    }
    isLocalChange.current = false;
  }, [marketValuePerSqft]);

  const handleSliderChange = (value) => {
    const val = value[0];
    isLocalChange.current = true;
    setLocalValue(val);
    setInputValue(String(val));
    debouncedUpdate(val);
  };

  const handleInputChange = (e) => {
    const raw = e.target.value.replace(/[^0-9]/g, "");
    setInputValue(raw);
    const num = parseInt(raw, 10);
    if (!isNaN(num) && num >= 0) {
      isLocalChange.current = true;
      setLocalValue(num);
      debouncedUpdate(num);
    }
  };

  const handleInputBlur = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    let num = parseInt(inputValue, 10);
    if (isNaN(num) || num < 200) num = 200;
    if (num > 10000) num = 10000;
    isLocalChange.current = true;
    setLocalValue(num);
    setInputValue(String(num));
    setMarketValuePerSqft(num); // Immediate update on blur
  };

  return (
    <Card className="shadow-md">
      <CardHeader className="p-4 sm:p-6">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
            <TrendingUp className="h-4 w-4 sm:h-5 sm:w-5 text-primary flex-shrink-0" />
            Market Analysis
          </CardTitle>
          {showAddButton && (
            <Button
              onClick={onAddClick}
              size="sm"
              className="gap-1.5 bg-primary hover:bg-primary/90 text-white shadow-sm"
            >
              <Plus className="h-4 w-4" />
              <span className="hidden sm:inline">Add</span>
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="p-4 sm:p-6 pt-0 space-y-4 sm:space-y-6">
        <div className="space-y-3">
          <Label htmlFor="market-value-slider" className="text-xs sm:text-sm">
            Similar Renovated Properties Value ($ per sq ft)
          </Label>
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
            <Slider
              id="market-value-slider"
              min={200}
              max={10000}
              step={10}
              value={[localValue]}
              onValueChange={handleSliderChange}
              className="flex-1"
              disabled={readOnly}
            />
            <div className="flex items-center gap-1 sm:min-w-[160px] justify-start sm:justify-end">
              <span className="text-sm font-semibold text-gray-700">$</span>
              <Input
                type="text"
                inputMode="numeric"
                value={inputValue}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                disabled={readOnly}
                className="w-20 h-8 text-sm font-semibold text-center px-1"
              />
              <span className="text-xs sm:text-sm text-gray-500 whitespace-nowrap">per sq ft</span>
            </div>
          </div>
        </div>
        {children}
      </CardContent>
    </Card>
  );
}
